import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categoryLabels } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="漫画未找到" />
        <Navbar />
        <main>
          <div className="pt-20">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '漫画未找到' }]} />
          </div>
          <div className="text-center py-32 px-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryLabels[comic.category] || comic.category;
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 5);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${categoryName} | 韩漫屋`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫屋',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanwu.com/comic/${comic.slug}`,
    },
  };

  const pageTitle = `${comic.title} - ${categoryName}`;
  const pageDesc = `${comic.title}：${comic.description}。作者${comic.author}，${comic.status}，共${comic.chapters}话。在韩漫屋免费在线观看。`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title}, ${categoryName}, 韩国漫画, 免费韩漫在线观看, 韩漫屋, ${comic.tags.join(', ')}`}
        canonicalUrl={`https://hanmanwu.com/comic/${comic.slug}`}
        ogType="article"
      />
      <SchemaOrg data={articleSchema} />

      <Navbar />

      <main>
        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        <article className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - ${categoryName}漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    {categoryName}
                  </span>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                  <div className="p-4 bg-background-100 rounded-lg">
                    <p className="text-xs text-foreground-400">作者</p>
                    <p className="text-sm font-semibold text-foreground-900 mt-1">{comic.author}</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <p className="text-xs text-foreground-400">评分</p>
                    <p className="text-sm font-semibold text-primary-600 mt-1">
                      <i className="ri-star-fill text-accent-500 mr-1" />
                      {comic.rating}
                    </p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <p className="text-xs text-foreground-400">章节</p>
                    <p className="text-sm font-semibold text-foreground-900 mt-1">共 {comic.chapters} 话</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <p className="text-xs text-foreground-400">阅读量</p>
                    <p className="text-sm font-semibold text-foreground-900 mt-1">
                      {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                    </p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg">
                    <p className="text-xs text-foreground-400">最近更新</p>
                    <p className="text-sm font-semibold text-foreground-900 mt-1">{comic.updatedAt}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-play-circle-line" />
                    开始阅读
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-bookmark-line" />
                    加入收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-share-forward-line" />
                    分享
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-14">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {Array.from({ length: Math.min(comic.chapters, 30) }, (_, i) => {
                  const chapterNum = comic.chapters - i;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-background-100 transition-all duration-200 cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-200">
                        第 {chapterNum} 话
                      </span>
                      <span className="text-xs text-foreground-400">
                        {i < 3 ? comic.updatedAt : '2026-07-' + String(30 - i).padStart(2, '0')}
                      </span>
                    </div>
                  );
                })}
              </div>
              {comic.chapters > 30 && (
                <div className="text-center mt-4">
                  <p className="text-sm text-foreground-400">仅显示最近30话，完整章节请开始阅读后查看</p>
                </div>
              )}
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-14">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.slug}`}
                      className="group block"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2.5">
                        <img
                          src={rc.cover}
                          alt={`${rc.title} - ${categoryName}漫画`}
                          title={`${rc.title} - 韩国漫画在线阅读`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                        {rc.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {rc.rating} · {rc.chapters}话
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}